/* eslint-disable no-await-in-loop */
// dependancys
import React, { useEffect, useState } from "reactn";
import PropTypes from "prop-types";

// components
import { MarketingContainer, MarketingSection } from "./Marketing.styled";
import { InfoContainer } from "../../../../_common/containers/containers.styled";
import FormGroup from "shared-anpost-components/components/form/form_group/FormGroup";
import InputGroup from "shared-anpost-components/components/form/input_group/InputGroup";
import Checkbox from "shared-anpost-components/components/form/checkbox/Checkbox";
import CheckboxLabel from "shared-anpost-components/components/form/checkbox/CheckboxLabel";
import FieldError from "shared-anpost-components/components/form/fieldError/FieldError";
import Loader from "shared-anpost-components/components/loader/Loader";
import GlobalError from "shared-anpost-components/components/globalError/GlobalError";
import Error from "../../../../_common/error/Error";

// APIs
import { updateMarketingConsents, getJWTInfo, updateProfilingConsent, getProfile } from "../../../../../services/Api";

// hooks
import appHooks from "../../../../../hooks/appHook";

// helpers
import { setCheckbox, setAllGlobalConsents, toggleMarketingConsents } from "./Marketing.helpers";

const Marketing = ({ marketingConsents, refreshGlobalVals }) => {
    const [loader, setLoader] = useState(false);
    const [apiError, setApiError] = useState(false);

    const validate = () => {
        return {};
    };
    const { values, errors } = appHooks(validate);

    const closeFunc = () => {
        setApiError(false);
    };

    const handleMarketingConsentUpdate = (event, displayText) => {
        const { name, checked } = event.target;
		const { applications, channels } = marketingConsents;

		const tmpValues = setCheckbox(values, name, checked);
		refreshGlobalVals(tmpValues);
		
		if (name !== "updateAllMarketingConsents") {
			// if the checked checkbox is different than opt out in for all and profiling
			// and opt in for all has been previously checked - uncheck it
			if (name !== "anpost_profiling" && values.updateAllMarketingConsents === true)	values.updateAllMarketingConsents = false;

			// if checkbox is for profiling toggle only that value
			// else toggle other checkboxes based on toggle values
			if (name === "anpost_profiling") {
				values["anpost_profiling"] = checked;
				refreshGlobalVals(values);
			} else {
				const tmpValues = toggleMarketingConsents(channels, applications, values, name, checked);
				refreshGlobalVals(tmpValues);
			}
		} 

		// if opt in for all has just been toggled, toggle the other checkboxes with the appropriate value
		if (name === "updateAllMarketingConsents") {
			const tmpValues = setAllGlobalConsents(values, marketingConsents.channels, marketingConsents.applications, checked);
			refreshGlobalVals(tmpValues);
		}

		submitConsents(name);	
		
		window.dataLayer.push({
			event: "marketingPreferenceToggles",
  			toggleSelected: `${displayText} - ${checked === true ? "opt in" : "opt out"}`
		});
    };

	const submitConsents = async (name) => {
        lockMarketingConsents();

		name === "anpost_profiling" ? await submitProfilingConsent() : await updateConsentPromise();
    };

	const updateConsentPromise = () => {
		setLoader(true);

        return new Promise(resolve => {
			const applications = marketingConsents.applications.map(consent => {
				return {
					applicationCode: consent.applicationCode,
					isSubscribed: values[`product-${consent.applicationCode}`]
				}
			});

			let channels = {};
			Object.keys(marketingConsents.channels).forEach(key => {
				channels[key] = values[`channel-${key}`];
			});
			
			// set the values to pass to the API
			const data = {
				UID: values.ciamUserId,
				applications,
				channels
			};
	
			// call the update maketing API
			getJWTInfo().then(() => {
				updateMarketingConsents(data)
					.then(res => {
						if (res.status === 200) {
							getProfile(values.ciamUserId).then(res => {
								if (res.status === 200) {
									sessionStorage.setItem("userProfile", JSON.stringify(res.data));
									setLoader(false);
									resolve();
								}
							});	
						} else {
							setApiError(true);
							setLoader(false);
						}
					})
					.catch(() => {
						setApiError(true);
						setLoader(false);
					});
			});
        });
    };

	const submitProfilingConsent = () => {
		setLoader(true);

		return new Promise(resolve => {
			// set the values to pass to the API
			const data = {
				UID: values.ciamUserId,
				consentCode: "anpost_profiling",
				isConsentGranted: values["anpost_profiling"]
			};
	
			// call the update maketing API
			getJWTInfo().then(() => {
				updateProfilingConsent(data)
					.then(res => {
						if (res.status === 200) {
							getProfile(values.ciamUserId).then(res => {
								if (res.status === 200) {
									sessionStorage.setItem("userProfile", JSON.stringify(res.data));
									setLoader(false);
									resolve();
								}
							});	
						} else {
							setApiError(true);
							setLoader(false);
						}
					})
					.catch(() => {
						setApiError(true);
						setLoader(false);
					});
			});
        });
    };

    // this sets a localStorage value so as to not prompt the user for marketing consents again throughout the product flows
    // They have now updated their marketing consents more than once so shouldn't see them again
    const lockMarketingConsents = () => {
        localStorage.setItem(`ciam-marketing-complete-${values.ciamUserId}`, true);
    };

    const checkboxElement = (displayText, value, id) => {
		// don't show My Account as product
		if (id === "product-anpost_myaccount") return;

		const checkboxText = displayText === "Push" ? "App notifications" : displayText === "Sms" ? "SMS" : displayText;

        return (
            <FormGroup classes="toggle" key={id}>
                <InputGroup classes="marketing-toggle toggle__checkbox--small" error={errors[id]}>
					<Checkbox 
						id={id} 
						name={id} 
						change={e => {
							handleMarketingConsentUpdate(e, displayText);
						}}
						value={values[id] || value} 
						checked={values[id] ? true : false} 
						required
					/>
					<CheckboxLabel 
						checkbox="true" 
						htmlFor={id} 
						children={<><span>{checkboxText}</span><span aria-hidden="true">{values[id] == true ? "Yes" : "No"}</span></>} 
					/>
                </InputGroup>
                {errors[id] && <FieldError error={errors[id]} />}
            </FormGroup>
        );
    };

    return (
        <>
            {loader && <Loader />}
            {apiError && <GlobalError closeFunc={closeFunc} />}
            <MarketingContainer>
                <>
                    {Object.keys(marketingConsents).length === 0 && (
                        <Error
                            errorHeading="Marketing Consents unavailable"
                            errorText="we have no marketing consents available for you. Please register for a product to enable marketing consent"
                        ></Error>
                    )}
                    <InfoContainer section="marketing">
                        <h4 className="h5">An Post</h4>
                        <p>
                            We would like to let you know about products, services and special offers from the An Post Group of Companies, that we think may be of interest to you, make your life
							easier or be of value to you. If you would like to receive these communications please indicate below:
                        </p>
						<MarketingSection>
							<div>
								<h5 className="h6">Channels</h5>
								<p>
									Select the channels you are interested in for which we can reach out to you:
								</p>
							</div>
							<div>
							{Object.keys(marketingConsents.channels).length >  0
								&& Object.keys(marketingConsents.channels).map(key => {
                                    return checkboxElement(key.charAt(0).toUpperCase() + key.slice(1), values[`channel-${key}`], `channel-${key}`)
                            	})
							}
							</div>
						</MarketingSection>
						<MarketingSection>
							<div>
								<h5 className="h6">Products</h5>
								<p>
									Select the products you are interested in for which we can reach out to you:
								</p>
							</div>
							<div>
							{marketingConsents.applications.length >  0 && marketingConsents.applications.map(consent => {
                                    return checkboxElement(consent.applicationName, values[`product-${consent.applicationCode}`], `product-${consent.applicationCode}`)
                            	})
							}
							</div>
						</MarketingSection>
						<MarketingSection>
							<div>
								<h5 className="h6">All Marketing</h5>
								<p>
									We may still send you important notifications about your account outside of your notification settings.
								</p>
							</div>
							<div>
                                {checkboxElement("Opt in for all marketing", false, "updateAllMarketingConsents")}
							</div>
						</MarketingSection>
						<MarketingSection>
							<div>
								<h5 className="h6">Profiling</h5>
								<p>
								Profiling is any kind of automated processing of personal data that involves analysing or predicting your behaviour, habits or interests.
								</p>
							</div>
							<div>
                                {checkboxElement("Opt in for profiling", values["anpost_profiling"], "anpost_profiling")}
							</div>
						</MarketingSection>
                    </InfoContainer>
                </>
            </MarketingContainer>
        </>
    );
};

export default Marketing;
Marketing.propTypes = {
    marketingConsents: PropTypes.object,
    refreshGlobalVals: PropTypes.func,
    setGlobalValue: PropTypes.func
};
